import {
  and,
  ControlProps,
  RankedTester,
  rankWith,
  uiTypeIs,
} from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { FormHelperText, TextField } from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { RenderersUIType } from "@home-health-notify/domain";
import { Constants, debounce, searchByNPI } from "hhn-commons";
import { merge } from "lodash";
import { ErrorObject } from "ajv";

const toNumber = (value: string) =>
  value === "" ? undefined : parseInt(value, 10);

const NPIInputRenderer = (props: ControlProps) => {
  const [mounted, setMounted] = useState(false);
  const [npiValue, setNpiValue] = useState<any>();
  const [npiDetails, setNpiDetails] = useState<string>();

  const appliedUiSchemaOptions = merge(
    {},
    props.config,
    props.uischema.options,
  );

  const newError: ErrorObject = {
    instancePath: "/providerInformation/providerNPI",
    message: "invalid NPI",
    schemaPath:
      "#/properties/providerInformation/properties/providerNPI/validateNPI",
    keyword: "validateNPI",
    params: {},
  };

  const addAdditionalError = () => {
    props.config.addAdditionalErrors(newError);
  };

  const clearAdditionalError = () => {
    props.config.clearAdditionalErrors(newError);
  };

  const validateNPI = async (value: string, force: boolean = false) => {
    if (value && (force || value.length === 10)) {
      const npiNumber = toNumber(value);
      if (npiNumber) {
        try {
          const results = await searchByNPI(npiNumber);

          if (results.resultCount !== 1) {
            addAdditionalError();
          } else {
            const details = results.results[0];
            setNpiDetails(
              `${details.fullName}, ${details.providerType}, ${details.addressPractice}`,
            );
            clearAdditionalError();
          }
        } catch (error) {
          addAdditionalError();
        }
      }
    } else {
      clearAdditionalError();
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChange = useCallback(
    debounce((value: any) => {
      setNpiDetails(undefined);
      handleChange(props.path, value);
      validateNPI(value, true);
    }, Constants.API_DEBOUNCE_DELAY_IN_MS),
    [],
  );

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNpiValue(toNumber(value));
    debouncedChange(toNumber(value));
  };

  const onBlur = (event: any) => {
    // Trigger immediate validation on blur
    validateNPI(event.target.value, true);
  };

  useEffect(() => {
    if (!mounted && props.data) {
      setNpiValue(props.data);
      validateNPI(props.data, true);
      setMounted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  const isValid = props.errors.length === 0;
  const errors = !isValid ? props.errors : null;
  const { rootSchema, i18nKeyPrefix, handleChange, enabled, ...rest } = props;

  return (
    <>
      <TextField
        fullWidth={!appliedUiSchemaOptions.trim}
        disabled={!enabled}
        {...rest}
        type="number"
        onBlur={onBlur}
        id="outlined-start-adornment"
        sx={{
          mb: 0.75,
          width: "100%",
        }}
        onChange={onChange}
        value={npiValue}
        error={!!props.errors}
      />
      {isValid && <FormHelperText error={false}>{npiDetails}</FormHelperText>}
      <FormHelperText error={!isValid}>{errors}</FormHelperText>
    </>
  );
};

export const npiInputTester: RankedTester = rankWith(
  5,
  and(uiTypeIs(RenderersUIType.NPIInput)),
);

export default withJsonFormsControlProps(NPIInputRenderer);
